import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Confirmation, DeleteConfirmation, Dropdown, Icon, ListViewMenu } from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import useAuth from '~/hooks/useAuth.js';
import ProjectMemberBillRateModal from './ProjectMemberBillRateModal';
import ProjectMemberCostRateModal from './ProjectMemberCostRateModal';

const Reasons = styled.ul`
  list-style: disc outside;
  padding-left: 1.25rem;
`;

export default function ProjectMemberActionsMenu({ project, projectMember, activeAdminCount, variant, onChange }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const confirmation = useConfirmation();
  const auth = useAuth();
  const [dialog, setDialog] = useState(null);

  const { id: projectMemberId, typeId, isActive, isActuallyActive } = projectMember;
  const isAdmin = typeId === 'administrator';
  // Mirrors the server's guard: only demoting the last *active* admin is refused — an inactive admin can
  // always be demoted, whoever else is on the project.
  const isOnlyActiveAdmin = isAdmin && isActive && activeAdminCount <= 1;

  const canEditBillRate = project.isBillable && !project.useRoles;
  // Viewing cost follows the project's margin permission; changing it stays a workspace-admin action.
  const canEditCostRate = auth.workspace.manage;
  const canViewCostRate = canEditCostRate || project.permissions.viewMargin;

  const patch = async (body, message) => {
    try {
      await api.www.workspaces(workspace.id).projectSettings(project.id).members(projectMemberId).upsert(body);
      toast.success(message);
      await onChange();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const memberName = projectMember.member.name;

  const confirm = (title, message) =>
    confirmation.prompt((resolve) => (
      <Confirmation title={title} resolve={resolve}>
        {message}
      </Confirmation>
    ));

  const handleToggleAdmin = async () => {
    const confirmed = await confirm(
      isAdmin ? 'Remove as Project Admin' : 'Set as Project Admin',
      isAdmin
        ? `Are you sure you want to remove ${memberName} as a Project Admin on this project?`
        : `Are you sure you want to make ${memberName} a Project Admin on this project?`,
    );
    if (!confirmed) return;

    await patch(
      { typeId: isAdmin ? 'collaborator' : 'administrator' },
      isAdmin ? 'Project Admin removed.' : 'Project Admin added.',
    );
  };

  // Activating is harmless, so only the deactivation direction is confirmed.
  const handleToggleActive = async () => {
    if (isActive) {
      const confirmed = await confirm(
        'Deactivate Team Member',
        `Are you sure you want to deactivate ${memberName} on this project?`,
      );
      if (!confirmed) return;
    }

    await patch({ isActive: !isActive }, isActive ? 'Team member deactivated.' : 'Team member activated.');
  };

  const handleRemove = async () => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        title="Remove Team Member"
        deleteLabel="Remove"
        resolve={async (result) => {
          if (result) {
            try {
              await api.www.workspaces(workspace.id).projectSettings(project.id).members(projectMemberId).delete();
              toast.success('Team member removed successfully.');
              await onChange();
            } catch ({ message }) {
              toast.error(message);
            }
          }
          resolve(result);
        }}>
        Are you sure you want to remove {projectMember.member.name} from this project?
      </DeleteConfirmation>
    ));
  };

  const admin = isAdmin
    ? {
        label: 'Remove as Project Admin',
        disabled: isOnlyActiveAdmin,
        tooltip: isOnlyActiveAdmin ? 'Projects must keep at least one active Project Admin.' : undefined,
      }
    : {
        label: 'Set as Project Admin',
        disabled: !isActuallyActive,
        tooltip: !isActuallyActive ? 'An inactive project member cannot be set as a Project Admin.' : undefined,
      };

  // A member can be inactive in the workspace while still active on the project. The status filter hides
  // those rows under "Inactive", so the action has to say why it won't help rather than open a modal that
  // changes nothing the user can see — reactivating them is a workspace concern, not a project one.
  const isWorkspaceActive = projectMember.member?.isActive !== false;

  const active = {
    label: isActive ? 'Deactivate from Team' : 'Activate',
    disabled: isAdmin || !isWorkspaceActive,
    tooltip: !isWorkspaceActive
      ? 'This workspace member is inactive.'
      : isAdmin
        ? 'Remove the Project Admin role before deactivating.'
        : undefined,
  };

  // The menu only mounts its items when it opens, so this runs once per open rather than once per row.
  const [removeReasons, setRemoveReasons] = useState(null);

  const checkRemoveDependencies = useCallback(
    async (ws) => {
      const reasons = [];
      if (isAdmin) reasons.push('A Project Admin cannot be removed.');

      const { data } = await ws.projectSettings(project.id).members(projectMemberId).hasDependencies();
      if (data.hasDependencies) reasons.push(...(data.reasons ?? []).filter(Boolean));

      setRemoveReasons(reasons);
      return reasons.length > 0;
    },
    [isAdmin, project.id, projectMemberId],
  );

  const removeTooltip = useMemo(() => {
    if (!removeReasons?.length) return undefined;
    if (removeReasons.length === 1) return removeReasons[0];

    return (
      <>
        <p>This member cannot be removed because:</p>
        <Reasons>
          {removeReasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </Reasons>
      </>
    );
  }, [removeReasons]);

  const items = (Item, DeleteItem, setIsOpen) => {
    const run = (fn) => () => {
      setIsOpen(false);
      fn();
    };
    return (
      <>
        <Item disabled={admin.disabled} tooltip={admin.tooltip} onClick={run(handleToggleAdmin)}>
          {admin.label}
        </Item>
        <Item disabled={active.disabled} tooltip={active.tooltip} onClick={run(handleToggleActive)}>
          {active.label}
        </Item>
        {canEditBillRate && (
          <Item
            onClick={run(() =>
              setDialog(
                <ProjectMemberBillRateModal
                  project={project}
                  projectMember={projectMember}
                  onSaved={onChange}
                  onClose={() => setDialog(null)}
                />,
              ),
            )}>
            Edit Bill Rate
          </Item>
        )}
        {canViewCostRate && (
          <Item
            onClick={run(() =>
              setDialog(
                <ProjectMemberCostRateModal
                  project={project}
                  projectMember={projectMember}
                  onSaved={onChange}
                  onClose={() => setDialog(null)}
                />,
              ),
            )}>
            {canEditCostRate ? 'Edit Cost Rate' : 'View Cost Rate'}
          </Item>
        )}
        <DeleteItem onCheckDependencies={checkRemoveDependencies} tooltip={removeTooltip} onClick={run(handleRemove)}>
          Remove from Team
        </DeleteItem>
      </>
    );
  };

  return (
    <>
      {variant === 'list' ? (
        <ListViewMenu>{({ setIsOpen }) => items(ListViewMenu.Item, ListViewMenu.DeleteItem, setIsOpen)}</ListViewMenu>
      ) : (
        <Dropdown align="right">
          {({ setIsOpen }) => (
            <>
              <Dropdown.Trigger>
                <Button isAnchor onClick={() => setIsOpen(true)}>
                  <Icon icon="ellipsis-h" />
                </Button>
              </Dropdown.Trigger>
              <Dropdown.Menu>{items(Dropdown.Item, Dropdown.DeleteItem, setIsOpen)}</Dropdown.Menu>
            </>
          )}
        </Dropdown>
      )}

      {dialog}
    </>
  );
}
